import clsx from "clsx";
import { useMemo } from "react";

import { Small, VideoPlayerHandle } from "@fronterahealth/frontera-ui-components";

import { ApiMediaCommentsCommentTypeChoices } from "@api/graphql/types-and-hooks";
import { EmojiButton } from "@pages/VideoSessionsV2/components/Utils/EmojiButton";
import { getEmojiCount } from "@pages/VideoSessionsV2/components/Utils/helper";
import { EMOJI_BUTTONS } from "@pages/VideoSessionsV2/const";
import { EmojiButtonInterface } from "@pages/VideoSessionsV2/types";
import { useAdminData } from "@providers/AdminDataProvider";
import { useVideoSessionData } from "@providers/VideoSessionProviderV2";

interface EmojiCommentButtonsProps {
  onEmojiClick: (emoji: EmojiButtonInterface) => void;
  disabled: boolean;
  playerRef: React.RefObject<VideoPlayerHandle | null>;
  commentCount?: string;
  mediaId: string;
  className?: string;
}

export const EmojiCommentButtonsv2: React.FC<EmojiCommentButtonsProps> = ({
  onEmojiClick,
  disabled = false,
  playerRef,
  commentCount,
  mediaId,
  className,
}) => {
  const { isVideoDetailsPage } = useVideoSessionData();
  const { isUserCaregiver, isUserRBT } = useAdminData();

  // if the user is an RBT or Caregiver — they are only allowed to ask questions
  const showOnlyQuestionButton = isUserRBT || isUserCaregiver;

  const handlePlayPause = (pause: boolean) => {
    if (!playerRef.current) return;
    const player = playerRef.current?.player;
    if (player) pause ? player.pause() : player.play();
  };

  const handleEmojiButtonClick = (emoji: EmojiButtonInterface) => {
    handlePlayPause(true);
    onEmojiClick(emoji);
  };

  const buttonList = useMemo(
    () =>
      isVideoDetailsPage && showOnlyQuestionButton
        ? EMOJI_BUTTONS.filter((item) => item.value === ApiMediaCommentsCommentTypeChoices.Question)
        : EMOJI_BUTTONS,
    [isVideoDetailsPage, showOnlyQuestionButton],
  );

  return (
    <div className={clsx("lg:-ml-4 flex flex-wrap justify-center  gap-2 sm:gap-4", className)}>
      <div className="flex gap-2">
        {buttonList.map((button) => (
          <EmojiButton
            key={button.value}
            button={button}
            onEmojiClick={handleEmojiButtonClick}
            disabled={disabled}
            count={commentCount && !mediaId ? getEmojiCount(button.value, commentCount) : null}
          />
        ))}
        {isVideoDetailsPage && showOnlyQuestionButton && (
          <div className="flex items-center justify-start">
            <Small className="italic">Questions will be sent directly to your BCBA.</Small>
          </div>
        )}
      </div>
    </div>
  );
};
